import api from "@/services/api";
import {
  reactive,
  computed,
  ComputedRef,
  DeepReadonly,
  Ref,
  toRef,
  watch,
} from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { setTag, setUser } from "@sentry/vue";
import { useAuthStore } from "@/store/auth";
import { useManageUsersTracking } from "@/services/gtm";
import Cookies from "universal-cookie";
import { EcommerceDatapointMetric } from "@/services/ecommerce";
import {
  OrganicSearchTermMetric,
  SearchTermMetric,
} from "@/services/search_terms";
import { MarketingTableMetricKey } from "@/services/marketing";
import { Analytics4Metric } from "@/services/website_performance_ga4";
import { DateTime } from "luxon";
import {
  SocialMediaPageMetric,
  SocialMediaPostMetric,
} from "@/services/social_media";

export enum UserRole {
  USER = "USER",
  MANAGER = "MANAGER",
}
export const SystemUser = {
  id: -1,
  name: "Fyr",
  role: "System user",
  organization: -1,
  organizationName: "Fyr",
  isActive: true,
} as User;

export interface User {
  id: number;
  name: string;
  role: UserRole | string;
  organization: number;
  organizationName: string;
  isActive: boolean;
  dateJoined?: DateTime;
  lastLogin?: DateTime;
  email?: string | null; // Only available for staff and superadmins - null otherwise
  profilePictureUrl?: string | null;
}

export interface ExtraOrganiationAccess {
  organization: number;
  role: UserRole;
}

// The agency that owns this user's Hub, resolved server-side and gated on
// membership (primary org or extra-access on the agency). Independent of the
// active org selection — drives Hub nav visibility and routing. null when the
// user belongs to no agency.
export interface HomeAgency {
  id: number;
  name: string;
  agencyHubEnabled: boolean;
}

export interface TableColumnMetrics {
  marketingPerformance?: {
    metrics: MarketingTableMetricKey[];
    pageSize: number;
    // Set once the user has saved a column selection that could have included
    // the Status column, so a later absence means "unchecked" rather than
    // "predates the feature". See MarketingPerformanceTable's metricColumns.
    statusColumnIntroduced?: boolean;
  };
  socialMediaPosts?: {
    metrics: SocialMediaPostMetric[];
    metricsForSharedUrl: SocialMediaPostMetric[];
    pageSize: number;
  };
  socialMediaPages?: {
    metrics: SocialMediaPageMetric[];
    metricsForSharedUrl: SocialMediaPageMetric[];
    pageSize: number;
  };
  searchTermReport?: { metrics: SearchTermMetric[]; pageSize: number };
  searchTermDetailPages?: {
    metrics: OrganicSearchTermMetric[];
    pageSize: number;
  };
  acquisition?: { metrics: Analytics4Metric[]; pageSize: number };
  pages?: { metrics: Analytics4Metric[]; pageSize: number };
  ecommerce?: { metrics: EcommerceDatapointMetric[]; pageSize: number };
  conversions?: {
    metrics: { account: number; conversions: string[] }[];
    pageSize: number;
  };
}

/**
 * Which metrics a view should show, given the user's saved preference for it.
 *
 * `isSharedUrl` selects which stored list applies, as a strict either/or: a
 * "Copy link" visit reads `metricsForSharedUrl` and must NOT fall back to the
 * viewer's own `metrics`, or the bar and the table resolve different sets on
 * the same page. An empty list on the applicable side falls through to the
 * view's defaults.
 */
export interface SavedMetricPreference {
  metrics?: readonly string[];
  metricsForSharedUrl?: readonly string[];
}

export function resolveSavedMetrics<M extends string>(
  saved: SavedMetricPreference | undefined,
  defaults: readonly M[],
  isSharedUrl = false,
): M[] {
  const savedMetrics = isSharedUrl
    ? saved?.metricsForSharedUrl
    : saved?.metrics;
  return savedMetrics?.length ? ([...savedMetrics] as M[]) : [...defaults];
}

export type DigestFrequency = "DAILY" | "WEEKLY" | "NEVER";

export interface EmailGroupPreferences {
  summaryDigest?: boolean;
  systemNotifications?: boolean;
}

export interface Profile extends User {
  email: string;
  enabledEmailNotifications: boolean;
  notificationDigestFrequency: DigestFrequency;
  emailGroupPreferences: EmailGroupPreferences;
  canOnboard: boolean;
  isStaff: boolean;
  isSuperuser: boolean;
  selectedOrganization: number | null;
  selectedHomeRoute: string | null;
  extraOrganizationAccesses: ExtraOrganiationAccess[];
  // The agency the Hub is anchored to (title / default landing). `homeAgency`
  // identifies *which* agency; `hasAgencyHub` decides *whether* to show the Hub.
  homeAgency: HomeAgency | null;
  // True when the user has at least one flag-enabled member agency. The single
  // source of truth for Hub availability (nav link, route guard, homepage
  // option) — matches the backend, which serves the hub if ANY member agency
  // is enabled, so it's correct even when the home agency's own flag is off.
  hasAgencyHub: boolean;
  columnPreference?: TableColumnMetrics;
}

// The org the session is currently acting in: the explicitly selected org, or
// the user's primary org when none is selected. Shared so Sentry, cookies, GTM
// and every consumer resolve the active org identically.
export function resolveActiveOrganizationId(
  profile: Pick<Profile, "selectedOrganization" | "organization">,
): number {
  return profile.selectedOrganization ?? profile.organization;
}

export const GET_PROFILE_URL = "/auth/users/me/";

function getProfile(): Promise<Profile> {
  return api.get(GET_PROFILE_URL).then((response) => {
    const user = response.data;
    const external = !isInternalEmail(user.email);
    const organizationId = resolveActiveOrganizationId(user);
    setUser({
      id: String(user.id),
      external,
      organization: organizationId,
    });
    setTag("is_external_user", external);
    setTag("organization.id", organizationId);
    const cookies = new Cookies();
    cookies.set("is-external-user", external, { path: "/" });
    cookies.set("organizationId", organizationId, { path: "/" });
    cookies.set("userId", user.id, { path: "/" });
    return user;
  });
}

export function useProfileQuery() {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: ["profile"],
      queryFn: getProfile,
      enabled: toRef(authStore, "isAuthed"),
    }),
  );
}

export const LIST_USERS_URL = "/auth/users/";

function listUsers(): Promise<User[]> {
  return api.get(LIST_USERS_URL).then((response) => response.data);
}

// Accept an optional reactive `enabled` flag so callers that only need
// the org user list behind a UI gate (e.g. a bulk-assign header that
// only renders on certain tabs) can defer the fetch until the gate is
// open. Default `true` keeps every existing call site eager.
export function useUsersQuery(
  enabled: Ref<boolean> | ComputedRef<boolean> | boolean = true,
) {
  return reactive(
    useQuery({
      queryKey: ["users"],
      queryFn: listUsers,
      enabled,
    }),
  );
}

// Candidate users for assignment pickers. `/auth/users/` intentionally
// returns the whole org (active + deactivated) for the user-management
// screen; an assignee dropdown must exclude deactivated users — the
// assign endpoints reject them with a 400, so listing them only lets a
// user pick someone the backend will refuse. Filter before mapping to a
// dropdown shape (which drops `isActive`). Already-assigned-but-now-
// inactive users still render via AssigneeControl's own placeholder path.
export function activeAssignableUsers(users: User[] | undefined): User[] {
  return (users ?? []).filter((user) => user.isActive);
}

export type UpdateUserAction =
  | "promote"
  | "demote"
  | "activate"
  | "deactivate"
  | "remove";

function updateUser({
  id,
  action,
}: {
  id: number;
  action: UpdateUserAction;
}): Promise<User> {
  return api
    .post(`/auth/users/${id}/${action}/`)
    .then((response) => response.data);
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient();
  const gtmUsers = useManageUsersTracking();
  return reactive(
    useMutation({
      mutationFn: updateUser,
      onError: (_error, vars) => {
        if (vars.action === "promote") gtmUsers.savePromote("Error");
        if (vars.action === "demote") gtmUsers.saveDemote("Error");
        if (vars.action === "activate") gtmUsers.saveActivate("Error");
        if (vars.action === "deactivate") gtmUsers.saveDeactivate("Error");
        if (vars.action === "remove") gtmUsers.saveRemove("Error");
      },
      onSuccess: (newUser, vars) => {
        if (vars.action === "promote") gtmUsers.savePromote("Success");
        if (vars.action === "demote") gtmUsers.saveDemote("Success");
        if (vars.action === "activate") gtmUsers.saveActivate("Success");
        if (vars.action === "deactivate") gtmUsers.saveDeactivate("Success");
        if (vars.action === "remove") {
          gtmUsers.saveRemove("Success");
          // Remove user from list since they no longer have access
          queryClient.setQueryData<User[]>(["users"], (users) =>
            users?.filter((user) => user.id !== vars.id),
          );
          return;
        }
        queryClient.setQueryData<User[]>(["users"], (users) =>
          users?.map((user) => (user.id === newUser.id ? newUser : user)),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });
      },
    }),
  );
}

function createUser(payload: {
  name: string;
  password: string;
  invite: number;
  token: string;
}): Promise<User> {
  return api.post("/auth/users/", payload).then((response) => response.data);
}

export function useCreateUserMutation() {
  return reactive(
    useMutation({
      mutationFn: createUser,
    }),
  );
}

function joinOrganization(payload: {
  invite: number;
  token: string;
}): Promise<void> {
  return api
    .post("/auth/join_organization/", payload)
    .then((response) => response.data);
}

export function useJoinOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: joinOrganization,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
      },
    }),
  );
}

function updateProfile(payload: {
  name?: string;
  enabledEmailNotifications?: boolean;
  notificationDigestFrequency?: DigestFrequency;
  emailGroupPreferences?: EmailGroupPreferences;
  selectedOrganization?: number | null;
  selectedHomeRoute?: string;
  columnPreference?: TableColumnMetrics;
}): Promise<Profile> {
  return api
    .patch(`/auth/users/me/`, payload)
    .then((response) => response.data);
}

function updateSelectedOrganizationProfile(payload: {
  selectedOrganization: number | null;
}): Promise<{ selectedOrganization: number | null }> {
  return api
    .patch(`/auth/users/me/`, payload)
    .then((response) => response.data);
}

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateProfile,
      onSuccess: (profile) => {
        queryClient.setQueryData<User>(["profile"], profile);
        queryClient.setQueryData<User[]>(["users"], (users) =>
          users?.map((user) => (user.id === profile.id ? profile : user)),
        );
      },
    }),
  );
}

function uploadProfilePicture(file: File): Promise<Profile> {
  const formData = new FormData();
  formData.append("file", file);
  return api
    .post("/auth/users/upload_profile_picture/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((response) => response.data);
}

export function useUploadProfilePictureMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: uploadProfilePicture,
      onSuccess: (profile) => {
        queryClient.setQueryData<Profile>(["profile"], profile);
        queryClient.setQueryData<User[]>(["users"], (users) =>
          users?.map((user) => (user.id === profile.id ? profile : user)),
        );
      },
    }),
  );
}

function deleteProfilePicture(): Promise<Profile> {
  return api
    .delete("/auth/users/delete_profile_picture/")
    .then((response) => response.data);
}

export function useDeleteProfilePictureMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteProfilePicture,
      onSuccess: (profile) => {
        queryClient.setQueryData<Profile>(["profile"], profile);
        queryClient.setQueryData<User[]>(["users"], (users) =>
          users?.map((user) => (user.id === profile.id ? profile : user)),
        );
      },
    }),
  );
}

const SELECTED_ORGANIZATION_STABLE_QUERY_ROOTS = new Set([
  "notices",
  "supportedCurrencies",
  "supportedTimezones",
  "version",
]);

function isSelectedOrganizationScopedQuery(
  queryKey: readonly unknown[],
): boolean {
  const root = queryKey[0];
  return (
    typeof root === "string" &&
    !SELECTED_ORGANIZATION_STABLE_QUERY_ROOTS.has(root)
  );
}

export function useUpdateSelectedOrganization(): (
  organizationId: number | null,
) => Promise<void> {
  const self = useProfileQuery();
  const queryClient = useQueryClient();
  return function updateSelectedOrganization(
    organizationId: number | null,
  ): Promise<void> {
    if (organizationId === self.data?.organization) {
      organizationId = null;
    }
    if (organizationId !== self.data?.selectedOrganization) {
      return updateSelectedOrganizationProfile({
        selectedOrganization: organizationId,
      }).then(({ selectedOrganization }) => {
        queryClient.setQueryData<Profile>(["profile"], (profile) =>
          profile ? { ...profile, selectedOrganization } : profile,
        );
        return queryClient
          .invalidateQueries({
            predicate: (query) =>
              isSelectedOrganizationScopedQuery(query.queryKey),
          })
          .then(() => undefined);
      });
    } else {
      return Promise.resolve();
    }
  };
}

/**
 * Indicates whether the current profile has the MANAGER role for the selected organization.
 *
 * When no organization is selected or the selected organization equals the primary organization, the primary profile role is used; otherwise the role is looked up from `extraOrganizationAccesses`.
 *
 * @returns `true` if the profile's role for the selected organization is `UserRole.MANAGER`, `false` otherwise.
 */
export function useIsManager(): ComputedRef<boolean> {
  const profile = useProfileQuery();
  return computed(() => {
    if (!profile.data) return false;

    // If no selected organization or selected organization is the primary one,
    // check the primary organization role
    if (
      profile.data.selectedOrganization === null ||
      profile.data.selectedOrganization === profile.data.organization
    ) {
      return profile.data.role === UserRole.MANAGER;
    }

    // For extra organization access, find the role in that organization
    const extraAccess = profile.data.extraOrganizationAccesses?.find(
      (access) => access.organization === profile.data!.selectedOrganization,
    );
    return extraAccess?.role === UserRole.MANAGER;
  });
}

/**
 * Indicates whether the current user's profile has staff privileges.
 *
 * @returns `true` if the current profile's `isStaff` flag is true, `false` otherwise.
 */
export function useIsStaff(): ComputedRef<boolean> {
  const profile = useProfileQuery();
  return computed(() => profile.data?.isStaff === true);
}

export function useIsSuperuser(): ComputedRef<boolean> {
  const profile = useProfileQuery();
  return computed(() => profile.data?.isSuperuser === true);
}

export function useIsSelf(user: Ref<DeepReadonly<User>>): ComputedRef<boolean> {
  const profile = useProfileQuery();
  return computed(() => profile.data?.id === user.value.id);
}

export function isInternalEmail(email: string): boolean {
  return email.endsWith("@lighthouse8.com") || email.endsWith("@fyr.ai");
}

export function useIsInternalUser(): ComputedRef<boolean> {
  const profile = useProfileQuery();
  return computed(() =>
    profile.data ? isInternalEmail(profile.data.email) : false,
  );
}

export function isRestrictedAccessibleUser(email: string): boolean {
  return isInternalEmail(email) || email.endsWith("@try.no");
}

export function useIsRestrictedAccessibleUser(): ComputedRef<boolean> {
  const profile = useProfileQuery();
  return computed(() =>
    profile.data ? isRestrictedAccessibleUser(profile.data.email) : false,
  );
}

export function getUserById(
  users: User[] | undefined,
  id: number,
): User | undefined {
  return users?.find((user) => user.id === id);
}

export function useUserById(id: Ref<number>): ComputedRef<User | undefined> {
  const users = useUsersQuery();

  return computed(() => {
    if (id.value < 1) return SystemUser;
    return getUserById(users.data, id.value);
  });
}

export function onSelectedOrganizationChange(
  callback: (selectedOrganization: number | null) => void,
) {
  const profile = useProfileQuery();
  watch(
    () => profile.data?.selectedOrganization,
    (selectedOrganization, previousSelectedOrganization) => {
      if (
        previousSelectedOrganization !== undefined &&
        selectedOrganization !== undefined
      ) {
        callback(selectedOrganization);
      }
    },
  );
}
